/**
 * 「字在哪张表上」与「这个字在这个词里读哪个音」—— **纯逻辑，全项目唯一口径**。
 *
 * ## 为什么需要这个文件（2026-10-15 用户核对后的改造）
 *
 * 用户的原话把两件事说到了一起：
 *
 * > "首先应该是以「字」为基本单位……这本书的后面有一张《识字表》……还有一张
 * >  《写字表》……**所以你要作好标识，就是它不管出现在哪张表，你都要做好标识，
 * >  在哪一本书上出现在哪张表上，这很重要**……当然，在同一本书上，会不会同一个字
 * >  同时出现在《识字表》或《写字表》上？**如果有，都要标识**。"
 *
 * > "如果一个字是多音字，那么你要能够识别出来，并进行标识，**并且针对不同的音
 * >  分别进行造词**，这样也可以专门听写多音字。"
 *
 * 改造前的实现把「1类字／2类字」做成了 `hanzi` 表上的一个列（一个字在一本书里
 * 只能有一个类别）—— 同一个字同时在两张表上时**后导入的会覆盖前一个**。那是错的：
 * "在哪张表上"是**收录这件事**的属性，不是字的属性。所以有了 `hanzi_listing`
 * （一行 = 一个字在某本课本的某年级某学期某张表上），而本文件就是它的纯逻辑层。
 *
 * ## 为什么放在 shared 而不是 api
 *
 * 解析器（`import-parser.ts`）要在**前端本地**先跑一遍给家长看"这一行有问题"，
 * 服务层写库时用的必须是同一套判定 —— 两份实现迟早漂移，一份共用则不会。
 */

import { lookupPinyin, splitChars, splitSyllables } from './pinyin.js';

// ---------------------------------------------------------------- 表类型

/**
 * 课本后面的三张表。
 *
 * | 取值 | 中文 | 落在哪张库表 |
 * | --- | --- | --- |
 * | `shizi` | 识字表（会认） | `hanzi_listing.list_type` |
 * | `xiezi` | 写字表（会写） | `hanzi_listing.list_type` |
 * | `cibiao` | 词语表 | `word.from_textbook = true`（词本身就在 `word` 表里） |
 *
 * ⚠️ `cibiao` **不写进 `hanzi_listing`**：那一行是"这个字在哪张**字**表上"，
 *    词语表装的是词不是字。词语表落成 `word.from_textbook = true`（既有的那一列，
 *    本次改造**保持不变**并确认仍然成立）。
 */
export const LIST_TYPES = ['shizi', 'xiezi', 'cibiao'] as const;
export type ListType = (typeof LIST_TYPES)[number];

/** **字**表（`hanzi_listing` 只装这两个；词语表不是"字表"）。 */
export const HANZI_LIST_TYPES = ['shizi', 'xiezi'] as const;
export type HanziListType = (typeof HANZI_LIST_TYPES)[number];

/** 表类型 → 界面上给家长看的名字。 */
export const LIST_TYPE_LABELS: Record<ListType, string> = {
  shizi: '识字表（会认）',
  xiezi: '写字表（会写）',
  cibiao: '词语表',
};

/** 表类型的短名字（徽章里用）。 */
export const LIST_TYPE_SHORT_LABELS: Record<ListType, string> = {
  shizi: '识字表',
  xiezi: '写字表',
  cibiao: '词语表',
};

/** 表类型的说明（界面上解释"这两张表有什么区别"）。 */
export const LIST_TYPE_HINTS: Record<ListType, string> = {
  shizi: '课本后面「识字表」里的字，本学期只要求会认、不要求会写。',
  xiezi: '课本后面「写字表」里的字，要求会写。',
  cibiao: '课本后面「词语表」里的词，录入后会自动关联到它包含的每一个字。',
};

/** 是不是"字表"（识字表 / 写字表）。 */
export function isHanziListType(value: string): value is HanziListType {
  return (HANZI_LIST_TYPES as readonly string[]).includes(value);
}

/** 认不出来的表类型原样返回（界面显示「—」而不是静默变成空）。 */
export function listTypeLabel(value: string | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—';
  return LIST_TYPE_LABELS[value as ListType] ?? value;
}

export function listTypeShortLabel(value: string | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—';
  return LIST_TYPE_SHORT_LABELS[value as ListType] ?? value;
}

/**
 * 把课本里那张表的**原始写法**归一成表类型。
 *
 * 认这些（家长粘贴时可能出现的样子）：
 *   `识字表` / `认字表` / `会认字` / `二类字` / `2类字` / `shizi` / `type2`
 *   `写字表` / `生字表` / `会写字` / `一类字` / `1类字` / `xiezi` / `type1`
 *   `词语表` / `词表` / `cibiao`
 *
 * 顺序有讲究：先认"识字/会认"那一边。「写字表」里也含"字表"两个字，
 * 而「识字表」里含"字"，泛化匹配放在最后。
 *
 * ⚠️ `1类字/2类字` 是**旧口径**（改造前 `hanzi.category` 的取值）：
 *    - 1类字 = 会写字 = 写字表 → `xiezi`
 *    - 2类字 = 会认字 = 识字表 → `shizi`
 *    保留它是为了让改造前写下来的导入文本仍然能用（不静默丢掉家长的数据）。
 */
export function toListType(raw: string | undefined | null): ListType | null {
  if (raw === undefined || raw === null) return null;
  const text = raw.replace(/\s/g, '').trim();
  if (text === '') return null;

  if (text.includes('识字') || text.includes('认字') || text.includes('会认')) return 'shizi';
  if (text.includes('写字') || text.includes('会写') || text === '生字表') return 'xiezi';
  if (text.includes('词语表') || text.includes('词表')) return 'cibiao';

  if (text === 'type2' || text === '2类字' || text === '二类字' || text === 'shizi') return 'shizi';
  if (text === 'type1' || text === '1类字' || text === '一类字' || text === 'xiezi') return 'xiezi';
  if (text === 'cibiao') return 'cibiao';
  return null;
}

// ---------------------------------------------------------------- 收录（listing）的唯一性判定

/**
 * 一条「字在某本书的某年级某学期某张表上」的收录记录。
 *
 * 这是 `hanzi_listing` 一行的**语义形状**（不依赖数据库类型，纯逻辑可测）。
 */
export interface HanziListingKey {
  /** 汉字本身（不是 id —— 判定要在"还没入库"时也能做）。 */
  charText: string;
  /** 课本版本（id 或引用键）。 */
  textbookRef: string;
  gradeCode: string;
  semester: string;
  listType: HanziListType;
}

/** 「同一本书同一学期同一张表里一个字只收录一次」的键（= `uq_hanzi_listing`）。 */
export function listingKeyOf(key: HanziListingKey): string {
  return [key.charText, key.textbookRef, key.gradeCode, key.semester, key.listType].join('\u0000');
}

/**
 * 按表收录的**唯一性判定**（纯函数）：给定已经存在的收录键，逐个判定新来的。
 *
 * 返回的 `verdict` 三种：
 *   · `create` —— 这一行是新的（**同一个字在另一张表 / 另一个学期上也走这里**）；
 *   · `skip`   —— 完全相同的收录已经有了（重复导入同一份数据 → 第二次全是 skip，
 *                这就是"整块粘贴 + 预览 + 幂等"里幂等的那一半）；
 *   · `merge`  —— 同一批里出现了两次同样的收录（文本自己有重复行）。
 */
export interface ListingVerdict {
  key: string;
  verdict: 'create' | 'skip' | 'merge';
  /** 文本内部的重复（`merge`）与库里的已存在（`skip`）要分开说，家长才知道去哪改。 */
  reason: string;
}

export function judgeListings(
  incoming: readonly HanziListingKey[],
  existingKeys: Iterable<string>,
): ListingVerdict[] {
  const existing = new Set(existingKeys);
  const seen = new Set<string>();
  return incoming.map((item) => {
    const key = listingKeyOf(item);
    if (existing.has(key)) {
      return { key, verdict: 'skip' as const, reason: '这本书这一学期这张表里已经有「这个字」了' };
    }
    if (seen.has(key)) {
      return { key, verdict: 'merge' as const, reason: '这一份粘贴里出现了两次同样的收录，已合并' };
    }
    seen.add(key);
    return { key, verdict: 'create' as const, reason: '新增' };
  });
}

/**
 * 一个字在**同一本书**上的收录情况（"它出现在哪几张表上"）。
 *
 * ⚠️ 这是用户在 2026-10-15 明确要求的能力："不管出现在哪张表，你都要做好标识"。
 *    同一个字同时出现在两张表上时，这里应当返回**两条**（而不是覆盖成一条）。
 */
export function listingsOfChar(
  charText: string,
  rows: readonly HanziListingKey[],
): HanziListingKey[] {
  return rows.filter((row) => row.charText === charText);
}

// ---------------------------------------------------------------- 词 ↔ 字 的读音

/**
 * 从"整词拼音"推出**每个位置上的字读什么**。
 *
 * 词语的 `pinyin` 是音节之间用**单个空格**分隔的（`shuǐ ní`），音节数必须与
 * 字数一致（`validateWordPinyin` 已经保证了这一点）。于是第 i 个字的读音就是
 * 第 i 个音节 —— 这是"一个词里这个字读哪个音"的**唯一来源**。
 *
 * 返回的数组**长度与字数一致**（不会缩短），拿不到的位置是空串：
 *   · 拼音为空 → 全是空串；
 *   · 音节数与字数不一致（历史脏数据）→ 逐位能对上的给读音，多出来的忽略、
 *     少掉的留空。**不猜**：宁可空着让界面显示"待填"，也不要编一个读音
 *     （听写读错音就是教错孩子）。
 *
 * `长(zhǎng)大` 与 `校(xiào)长(zhǎng)` 是同一个字在两个词里读同一个音；
 * `长(cháng)短` 是另一个音 —— 三者的区别**只存在于这一层**。
 */
export function readingsFromWordPinyin(wordText: string, pinyin: string): string[] {
  const chars = splitChars(wordText);
  const syllables = splitSyllables(pinyin);
  return chars.map((_, index) => syllables[index] ?? '');
}

/**
 * 词里第 `position` 个字的读音，**取不到时回落到这个字自己的生效读音**。
 *
 * ⚠️ 回落只发生在"整词拼音拿不到"时。家长给整词写对了拼音（`cháng dà`）却因为
 *    某个字没被关联就丢了读音，是比"回落成默认读音"更糟的错（前者静默丢数据）。
 */
export function effectiveReading(
  wordText: string,
  wordPinyin: string,
  charText: string,
  fallback: string,
): string {
  const position = splitChars(wordText).indexOf(charText);
  if (position < 0) return fallback;
  const fromWord = readingsFromWordPinyin(wordText, wordPinyin)[position];
  return fromWord !== undefined && fromWord !== '' ? fromWord : fallback;
}

/**
 * 一个字的**全部读音**（自动读音 + 备选读音 + 家长覆盖的那些）。
 *
 * 为什么要把覆盖值也算进来：家长可能人工填一个内置字表里没有、但确实存在的读音
 * （生僻姓氏、地名）。用它去校验"词里这个字读这个音合法吗"时，把人工值排除在外
 * 会让家长**没法录进去**。
 */
export function availableReadings(
  charText: string,
  overrides: readonly (string | null | undefined)[] = [],
): string[] {
  const found = lookupPinyin(charText);
  const out: string[] = [];
  const push = (value: string | null | undefined): void => {
    const text = (value ?? '').trim();
    if (text !== '' && !out.includes(text)) out.push(text);
  };
  push(found.primary);
  for (const alt of found.alternates) push(alt);
  for (const override of overrides) push(override);
  return out;
}

/**
 * 这个字是不是多音字（**技术层**：读音集合里有 2 个及以上）。
 *
 * 判据是**读音集合里有 2 个及以上**，而且**至少有一个不是自动读音**
 * （只看"内置表里有几个读音"会把"内置表给了 3 个读音、家长只用其中 1 个"也算成
 * 多音字，那种"多音字"在听写上毫无意义）。
 *
 * ⚠️ 2026-10-18：**不要**拿它当教学口径的「多音字」用（列表徽章 / 「只看多音字」筛选）。
 *    内置拼音表把**变调**（一 yī/yí/yì、不 bù/bú）、**古音**（七 qī/qí）、
 *    **生僻音**（上 shàng/shǎng）都算成"读音"，项目里 280 个一年级生字会被它
 *    标出 134 个多音字。教学口径是 `hanzi.polyphonic`（管理员**人工标注**，默认 false）。
 *    本函数保留给：读音选择、按音造词、听写多音字专项、导入时的"多音字重复"判定。
 */
export function isPolyphonic(
  charText: string,
  overrides: readonly (string | null | undefined)[] = [],
): boolean {
  const readings = availableReadings(charText, overrides);
  if (readings.length < 2) return false;
  const primary = lookupPinyin(charText).primary;
  return readings.some((reading) => reading !== primary);
}

/**
 * **单音字的自动读音**（2026-10-20：「上传文件可以只写字」那一轮）。
 *
 * ## 为什么需要它（用户的目标："100% 和书上一样" + "最省事"）
 *
 * 家长把课本后面的《识字表》交给助手生成 .txt 时，**没必要**为每个字都写一遍
 * 拼音 —— 一个字如果字典里只有一种读法，写与不写都是一样的。所以：
 *
 *   · 恰好 **1** 个读音 → 它就是唯一可能的那一个，**直接填**（不是猜）；
 *   · **0** 个（内置表没收录）→ 空串：没有读音可填，界面显示"待填"；
 *   · **≥2** 个（多音字）→ 空串：**绝不猜**。"长"读 cháng 还是 zhǎng 由词决定，
 *     猜错就是教错孩子（这是本项目反复强调的那条铁律）。
 *
 * 判据只有一份：`availableReadings()`（主读音 + 备选读音 + 人工覆盖）。
 * 导入时"这个位置算不算已标音"用的就是这一份，所以**单音字不会再要求家长标音**；
 * 多音字仍然算"未标音"（重复出现时预览会要求选音；只出现一次时按既有规则不拦保存，
 * 但界面上的读音是"待填"）。
 *
 * ⚠️ 内置表里"读音"包得很宽（变调 `一 yī/yí/yì`、古音、生僻异读都算），
 *    所以按本函数判出来的"单音字"比教学口径（`hanzi.polyphonic` 人工标注）**少**，
 *    多音字**多**。这是既有口径，不在这里重定义（见 `isPolyphonic` 的 ⚠️）。
 */
export function autoReadingForChar(charText: string): string {
  const readings = availableReadings(charText);
  return readings.length === 1 ? readings[0]! : '';
}

/** 判断一个读音是不是这个字**可用**的读音之一（写入 `reading` 前的校验）。 */
export function isReadingOfChar(
  charText: string,
  reading: string,
  overrides: readonly (string | null | undefined)[] = [],
): boolean {
  return availableReadings(charText, overrides).includes(reading.trim());
}

// ---------------------------------------------------------------- 隐藏读音（显示口径）

/**
 * **一个字最多能隐藏几个读音**（界面、zod、服务层、数据库列共用的唯一上限）。
 *
 * 为什么要有上限：`hanzi.hidden_readings` 是 `text[]`，客户端可以塞任意长的数组。
 * 内置拼音表里一个字最多也就六七个读音，16 已经很宽松；超过它就是坏数据
 * （或者有人拿这一列当存储用），一律拒绝。
 *
 * ⚠️ 前端（`lib/hanziReadingPrefs.ts`）与后端（zod + 服务层）**引的是这一份**，
 *    所以"界面上最多能隐藏几个"与"接口最多收几个"不可能走岔。
 */
export const MAX_HIDDEN_READINGS = 16;

/** `normalizeHiddenReadings()` 的结果：要么给一份干净的数组，要么说清哪里不对。 */
export type NormalizedHiddenReadings =
  | { ok: true; hidden: string[] }
  | { ok: false; reason: string; invalid: string[] };

/**
 * 把「隐藏了哪几个读音」整理成**可以写库**的那一份 —— 这是唯一的校验口径。
 *
 * 规则（每一条都对应一个真实会发生的输入）：
 *   1. 空串 / 纯空白 = 没写（不是错误，直接丢掉）—— 界面上不会产生它，
 *      但手工拼请求体的人会；
 *   2. **去重**（同一个音写两遍只留一个，顺序按第一次出现）；
 *   3. 每个值必须是 `availableReadings(charText, overrides)` 里的一个 ——
 *      否则要么是别的字的读音，要么是手抖打错的拼音；
 *   4. 最多 `MAX_HIDDEN_READINGS` 个。
 *
 * `overrides` 传**这个字当前的人工覆盖拼音**（`hanzi.pinyin`）：家长可能填一个
 * 内置字表里没有、但确实存在的读音，它也算"可以隐藏的音"（与按音造词同一条口径）。
 *
 * ⚠️ 空数组是合法结果 —— 它等于**全部显示**，也正是老数据/新列的默认值。
 */
export function normalizeHiddenReadings(
  charText: string,
  hidden: readonly (string | null | undefined)[],
  overrides: readonly (string | null | undefined)[] = [],
): NormalizedHiddenReadings {
  const allowed = availableReadings(charText, overrides);
  const out: string[] = [];
  const invalid: string[] = [];
  /** 非空项个数（含重复项）—— 上限按**请求里有几项**算，这样它才是一条能触发的守卫。 */
  let filled = 0;

  for (const raw of hidden) {
    const value = (raw ?? '').trim();
    if (value === '') continue;
    filled += 1;
    if (filled > MAX_HIDDEN_READINGS) {
      return {
        ok: false,
        invalid: [],
        reason: `最多隐藏 ${MAX_HIDDEN_READINGS} 个读音`,
      };
    }
    if (!allowed.includes(value)) {
      if (!invalid.includes(value)) invalid.push(value);
      continue;
    }
    if (!out.includes(value)) out.push(value);
  }

  if (invalid.length > 0) {
    const choices = allowed.length > 0 ? allowed.join(' / ') : '内置字表里没有这个字';
    return {
      ok: false,
      invalid,
      reason:
        invalid.length === 1
          ? `「${invalid[0]}」不是「${charText}」的读音（可选：${choices}）`
          : `这几个不是「${charText}」的读音：${invalid.join('、')}（可选：${choices}）`,
    };
  }

  return { ok: true, hidden: out };
}

/**
 * 按「当前这个字有哪些读音」把隐藏的那几项切出来（界面渲染用）。
 *
 * ⚠️ **只按当前读音集合切**：隐藏记录里如果还留着这个字已经不再有的读音
 *    （换过字 / 拼音库变了 / 家长改了拼音覆盖），它既不会出现在"可见"里，
 *    也不会出现在"已隐藏"里 —— 不报错、也不静默把一个读音藏掉。
 * ⚠️ 顺序 = 传进来的 `all` 的顺序（主读音在前），界面上不重排。
 */
export function splitReadingsByHidden(
  all: readonly string[],
  hidden: readonly string[],
): { visible: string[]; hidden: string[] } {
  const hiddenSet = new Set(hidden);
  return {
    visible: all.filter((reading) => !hiddenSet.has(reading)),
    hidden: all.filter((reading) => hiddenSet.has(reading)),
  };
}

// ---------------------------------------------------------------- 按读音归组

/** 一个字在某个词里的读音关联（`word_hanzi` 一行的语义形状）。 */
export interface WordReadingLink {
  wordId: string;
  wordText: string;
  /** 这个字在词里的位置（从 0 开始）。 */
  position: number;
  /** 这个字在这个词里的读音（多音字按词定音）。 */
  reading: string;
}

/**
 * 把一批「词 ↔ 字」关联**按读音归组**：`字 → 读音 → 关联`。
 *
 * 这就是用户要的那件事："**能查这个字在哪些词里读 cháng、在哪些词里读 zhǎng**"。
 * 键的第一层是**字**（因为"多音字按音造词"是围绕字说的），第二层是读音。
 *
 * 同一个词里同一个字出现两次（`长长久久`）时两行都会被收进来 —— 位置不同，
 * 它们确实是两条关联；**不按词 id 去重**（去重是展示层的事，见
 * `summarizeReadings` 的 `wordCountByReading`）。
 */
export function groupByHanziThenReading(
  charTextOf: (hanziId: string) => string,
  links: readonly (WordReadingLink & { hanziId: string })[],
): Map<string, Map<string, WordReadingLink[]>> {
  const out = new Map<string, Map<string, WordReadingLink[]>>();
  for (const link of links) {
    const char = charTextOf(link.hanziId);
    const byReading = out.get(char) ?? new Map<string, WordReadingLink[]>();
    const bucket = byReading.get(link.reading) ?? [];
    bucket.push(link);
    byReading.set(link.reading, bucket);
    out.set(char, byReading);
  }
  return out;
}

/** 汉字 → 它的多音字信息（界面上"这个字有哪几个音、各有哪些词"）。 */
export interface HanziReadingSummary {
  charText: string;
  /** 内置表 + 人工覆盖的全部读音（最常用在前）。 */
  readings: string[];
  /** 主读音（内置表第一项；未收录时是空串）。 */
  primary: string;
  /** 是否多音字（**技术层**，见 `isPolyphonic` 的判据；不是教学口径的标注）。 */
  polyphonic: boolean;
  /** 每个读音下**已经造过词**的数量（没有词的读音不在这个对象里）。 */
  wordCountByReading: Record<string, number>;
  /** 还没造过任何词的读音（家长录完字表之后的"待办"）。 */
  readingsWithoutWords: string[];
}

/**
 * 汇总一个字的读音情况（界面用它渲染"多音字 · 两个音各造了几个词"）。
 *
 * `links` 是**这个字**的关联（同一批查询里已经按 `hanzi_id` 取好），
 * `wordTextOf` 把词 id 变成词面（用于去重：同一个词关联两次只算一个）。
 */
export function summarizeReadings(
  charText: string,
  overrides: readonly (string | null | undefined)[],
  links: readonly { wordId: string; reading: string }[],
): HanziReadingSummary {
  const readings = availableReadings(charText, overrides);
  const primary = lookupPinyin(charText).primary;

  const wordsByReading = new Map<string, Set<string>>();
  for (const link of links) {
    const reading = link.reading.trim();
    if (reading === '') continue;
    const bucket = wordsByReading.get(reading) ?? new Set<string>();
    bucket.add(link.wordId);
    wordsByReading.set(reading, bucket);
  }

  const wordCountByReading: Record<string, number> = {};
  for (const [reading, words] of wordsByReading) wordCountByReading[reading] = words.size;

  return {
    charText,
    readings,
    primary,
    polyphonic: isPolyphonic(charText, overrides),
    wordCountByReading,
    /** ⚠️ 只在**读音集合非空**时才有意义：一个字表里没收录的字不该报"待造词"。 */
    readingsWithoutWords: readings.filter((reading) => (wordCountByReading[reading] ?? 0) === 0),
  };
}
